import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { ApiError, api } from '@/lib/api';

/**
 * «Оценки и комментарии».
 *
 * Две справочные таблицы:
 *   1) Комментарии — для 0–2 классов (вместо цифровых оценок). У каждого
 *      комментария есть короткий код (рисуется в ячейке журнала) и текст
 *      (показывается в попапе выставления).
 *   2) Диапазоны опозданий — для всех классов (выбираются вместо ручного ввода
 *      минут).
 *
 * Редактирование inline: каждая строка — форма, при изменении подсвечивается
 * кнопка «Сохранить»; до этого момента изменения не отправляются на сервер.
 */
export default function GradingPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('grading_settings', 'write');
  const canDelete = can('grading_settings', 'delete');

  const [comments, setComments]   = useState([]);
  const [ranges, setRanges]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    Promise.all([
      api.get('/api/grade-comments?include_archive=1'),
      api.get('/api/late-ranges?include_archive=1'),
    ]).then(([c, r]) => {
      setComments(c.rows || []);
      setRanges(r.rows || []);
    }).catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Topbar
        breadcrumb={['Обучение', 'Оценки и комментарии']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-content" style={{ padding: '20px 24px 40px' }}>
          {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : (
            <>
              <CommentsSection
                rows={comments}
                setRows={setComments}
                canWrite={canWrite}
                canDelete={canDelete}
              />
              <RangesSection
                rows={ranges}
                setRows={setRanges}
                canWrite={canWrite}
                canDelete={canDelete}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ===== Комментарии для 0–2 классов =====

function CommentsSection({ rows, setRows, canWrite, canDelete }) {
  const onAdd = () => {
    const draft = {
      _draft: true, _key: Math.random(),
      id: null, text: '', color: '#2e7d32',
      sortOrder: rows.length ? Math.max(...rows.map((r) => r.sortOrder || 0)) + 10 : 10,
      archive: false,
    };
    setRows((prev) => [...prev, draft]);
  };

  const onSave = async (row) => {
    const payload = {
      text:      row.text,
      color:     row.color || null,
      sortOrder: row.sortOrder,
      archive:   row.archive,
    };
    try {
      const res = row.id
        ? await api.put('/api/grade-comments/' + row.id, payload)
        : await api.post('/api/grade-comments', payload);
      const saved = res.comment;
      setRows((prev) => {
        if (!row.id) {
          return prev.map((r) => r._key === row._key ? { ...saved, _key: r._key } : r);
        }
        return prev.map((r) => r.id === row.id ? saved : r);
      });
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось сохранить');
    }
  };

  const onDelete = async (row) => {
    if (!row.id) {
      setRows((prev) => prev.filter((r) => r._key !== row._key));
      return;
    }
    if (!confirm(`Удалить комментарий «${row.text}»?`)) return;
    try {
      await api.delete('/api/grade-comments/' + row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    }
  };

  const update = (key, patch) => {
    setRows((prev) => prev.map((r) =>
      (r.id ? r.id === key : r._key === key) ? { ...r, ...patch } : r
    ));
  };

  return (
    <section className="grd-section">
      <header className="grd-section__head">
        <div>
          <h2 className="grd-section__title">Комментарии (0–2 классы)</h2>
          <p className="grd-section__sub">
            В ячейке журнала комментарий обозначается буквой «К» с подсветкой выбранного цвета;
            при наведении — полный текст. Текст «вмораживается» в отметку при сохранении (хранится
            в <code>journal_marks.comment</code>), поэтому переименование/удаление комментария
            здесь не меняет тултип уже выставленных «К». Цвет, наоборот, подтягивается из этого
            справочника по совпадению текста — если поменять цвет здесь, ячейки в журнале
            перекрасятся; если переименовать комментарий, старые «К» останутся со своим текстом,
            но станут серыми (нет совпадения с новым названием).
          </p>
        </div>
        {canWrite && (
          <button className="btn btn--primary btn--sm" onClick={onAdd}>
            <Icon name="plus" size={13} /> Добавить
          </button>
        )}
      </header>

      {rows.length === 0 ? (
        <div className="ref-empty" style={{ padding: 32 }}>
          Список пуст. Добавьте первый комментарий.
        </div>
      ) : (
        <div className="grd-list">
          <div className="grd-row grd-row--head grd-row--comments">
            <div>Цвет</div>
            <div>Текст</div>
            <div>Порядок</div>
            <div>Архив</div>
            <div />
          </div>
          {rows.map((r) => (
            <CommentRow
              key={r.id || r._key}
              row={r}
              canWrite={canWrite}
              canDelete={canDelete}
              onChange={(patch) => update(r.id || r._key, patch)}
              onSave={() => onSave(r)}
              onDelete={() => onDelete(r)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CommentRow({ row, canWrite, canDelete, onChange, onSave, onDelete }) {
  const [pristine] = useState(() => JSON.stringify({
    text: row.text, color: row.color, sortOrder: row.sortOrder, archive: row.archive,
  }));
  const dirty = row._draft || JSON.stringify({
    text: row.text, color: row.color, sortOrder: row.sortOrder, archive: row.archive,
  }) !== pristine;

  return (
    <div className={'grd-row grd-row--comments' + (row.archive ? ' grd-row--archived' : '')}>
      <div className="grd-color-cell">
        <span className="grd-pill" style={{ background: row.color || 'var(--panel-2)' }}>К</span>
        <input
          type="color"
          value={row.color || '#999999'}
          onChange={(e) => onChange({ color: e.target.value })}
          disabled={!canWrite}
        />
      </div>
      <div>
        <input
          type="text"
          value={row.text}
          placeholder="Полный текст комментария"
          onChange={(e) => onChange({ text: e.target.value })}
          disabled={!canWrite}
          style={{ width: '100%' }}
        />
      </div>
      <div>
        <input
          type="number"
          value={row.sortOrder}
          min={0}
          max={9999}
          onChange={(e) => onChange({ sortOrder: Number(e.target.value) })}
          disabled={!canWrite}
          style={{ width: 64 }}
        />
      </div>
      <div>
        <label className="ref-checkbox" style={{ fontSize: 12 }}>
          <input
            type="checkbox"
            checked={!!row.archive}
            onChange={(e) => onChange({ archive: e.target.checked })}
            disabled={!canWrite}
          />
          в архиве
        </label>
      </div>
      <div className="grd-row__actions">
        {canWrite && (
          <button
            className={'btn btn--sm ' + (dirty ? 'btn--primary' : 'btn--ghost')}
            disabled={!dirty}
            onClick={onSave}
            title={dirty ? 'Сохранить' : 'Нет изменений'}
          >
            <Icon name="check" size={12} /> Сохранить
          </button>
        )}
        {(canWrite || canDelete) && (
          <button
            className="btn btn--sm btn--ghost"
            onClick={onDelete}
            title={row.id ? 'Удалить' : 'Отменить'}
            disabled={!canDelete && !!row.id}
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

// ===== Диапазоны опозданий =====

function RangesSection({ rows, setRows, canWrite, canDelete }) {
  const onAdd = () => {
    const draft = {
      _draft: true, _key: Math.random(),
      id: null, label: '', minMinutes: null, maxMinutes: null,
      sortOrder: rows.length ? Math.max(...rows.map((r) => r.sortOrder || 0)) + 10 : 10,
      archive: false,
    };
    setRows((prev) => [...prev, draft]);
  };

  const onSave = async (row) => {
    const payload = {
      label:       row.label,
      minMinutes:  row.minMinutes,
      maxMinutes:  row.maxMinutes,
      sortOrder:   row.sortOrder,
      archive:     row.archive,
    };
    try {
      const res = row.id
        ? await api.put('/api/late-ranges/' + row.id, payload)
        : await api.post('/api/late-ranges', payload);
      const saved = res.range;
      setRows((prev) => {
        if (!row.id) {
          return prev.map((r) => r._key === row._key ? { ...saved, _key: r._key } : r);
        }
        return prev.map((r) => r.id === row.id ? saved : r);
      });
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось сохранить');
    }
  };

  const onDelete = async (row) => {
    if (!row.id) {
      setRows((prev) => prev.filter((r) => r._key !== row._key));
      return;
    }
    if (!confirm(`Удалить диапазон «${row.label}»?`)) return;
    try {
      await api.delete('/api/late-ranges/' + row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    }
  };

  const update = (key, patch) => {
    setRows((prev) => prev.map((r) =>
      (r.id ? r.id === key : r._key === key) ? { ...r, ...patch } : r
    ));
  };

  return (
    <section className="grd-section" style={{ marginTop: 28 }}>
      <header className="grd-section__head">
        <div>
          <h2 className="grd-section__title">Диапазоны опозданий</h2>
          <p className="grd-section__sub">
            Используются вместо ручного ввода минут на всех ступенях. Хранится текстовая
            метка, поэтому удаление диапазона не сломает уже выставленные пометки.
          </p>
        </div>
        {canWrite && (
          <button className="btn btn--primary btn--sm" onClick={onAdd}>
            <Icon name="plus" size={13} /> Добавить
          </button>
        )}
      </header>

      {rows.length === 0 ? (
        <div className="ref-empty" style={{ padding: 32 }}>
          Список пуст. Добавьте первый диапазон.
        </div>
      ) : (
        <div className="grd-list">
          <div className="grd-row grd-row--head grd-row--ranges">
            <div>Подпись</div>
            <div>От, мин.</div>
            <div>До, мин.</div>
            <div>Порядок</div>
            <div>Архив</div>
            <div />
          </div>
          {rows.map((r) => (
            <RangeRow
              key={r.id || r._key}
              row={r}
              canWrite={canWrite}
              canDelete={canDelete}
              onChange={(patch) => update(r.id || r._key, patch)}
              onSave={() => onSave(r)}
              onDelete={() => onDelete(r)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function RangeRow({ row, canWrite, canDelete, onChange, onSave, onDelete }) {
  const [pristine] = useState(() => JSON.stringify({
    label: row.label, minMinutes: row.minMinutes, maxMinutes: row.maxMinutes,
    sortOrder: row.sortOrder, archive: row.archive,
  }));
  const dirty = row._draft || JSON.stringify({
    label: row.label, minMinutes: row.minMinutes, maxMinutes: row.maxMinutes,
    sortOrder: row.sortOrder, archive: row.archive,
  }) !== pristine;

  const setNumOrNull = (v) => v === '' ? null : Number(v);

  return (
    <div className={'grd-row grd-row--ranges' + (row.archive ? ' grd-row--archived' : '')}>
      <div>
        <input
          type="text"
          value={row.label}
          placeholder="напр. < 5 минут"
          onChange={(e) => onChange({ label: e.target.value })}
          disabled={!canWrite}
          style={{ width: 200 }}
        />
      </div>
      <div>
        <input
          type="number"
          value={row.minMinutes ?? ''}
          min={0}
          max={999}
          onChange={(e) => onChange({ minMinutes: setNumOrNull(e.target.value) })}
          disabled={!canWrite}
          style={{ width: 80 }}
        />
      </div>
      <div>
        <input
          type="number"
          value={row.maxMinutes ?? ''}
          min={0}
          max={999}
          placeholder="∞"
          onChange={(e) => onChange({ maxMinutes: setNumOrNull(e.target.value) })}
          disabled={!canWrite}
          style={{ width: 80 }}
        />
      </div>
      <div>
        <input
          type="number"
          value={row.sortOrder}
          min={0}
          max={9999}
          onChange={(e) => onChange({ sortOrder: Number(e.target.value) })}
          disabled={!canWrite}
          style={{ width: 64 }}
        />
      </div>
      <div>
        <label className="ref-checkbox" style={{ fontSize: 12 }}>
          <input
            type="checkbox"
            checked={!!row.archive}
            onChange={(e) => onChange({ archive: e.target.checked })}
            disabled={!canWrite}
          />
          в архиве
        </label>
      </div>
      <div className="grd-row__actions">
        {canWrite && (
          <button
            className={'btn btn--sm ' + (dirty ? 'btn--primary' : 'btn--ghost')}
            disabled={!dirty}
            onClick={onSave}
          >
            <Icon name="check" size={12} /> Сохранить
          </button>
        )}
        {(canWrite || canDelete) && (
          <button
            className="btn btn--sm btn--ghost"
            onClick={onDelete}
            title={row.id ? 'Удалить' : 'Отменить'}
            disabled={!canDelete && !!row.id}
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
